import moment from "moment";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { capitalize } from "lodash";
import { Select, SelectOption, InputLabel, Box, Tooltip, Divider } from "@launchmetrics/lm-ui";

import { useReportSettings } from "../hooks";
import { PERIOD_MINIMUM_DATE } from "./consts";
import { State } from "store/types";
import { InfoCircleOutline } from '@launchmetrics/lm-icons';
import { useStyles } from "components/Article/Report/styles";

import '../../ReportConfiguration.scss';
import { ComparePeriodsMode, REPORT_COMPARE_PERIODS_MODES, ReportDefinitionSettings } from "store/models/reportConfiguration";

type PeriodItem = { label: string, value: string };

export const Period: React.FC = () => {
	const { t } = useTranslation();
	const { reportForm } = useStyles();

	const disabled = useSelector((state: State) => state.article.report.formDisabled);
	const {begin_date: filterBeginDate, end_date: filterEndDate} = useSelector((state: any) => state.search.horizontalFilters);
	const [reportSettings, setReportSettings] = useReportSettings();

	const getMonthsItems = useCallback((fromDate: moment.MomentInput, toDate: moment.MomentInput = moment(PERIOD_MINIMUM_DATE, 'YYYY-MM-DD')): PeriodItem[] => {
		const maxDate = moment(fromDate);
		const minDate = moment(toDate);
		const monthItems = [{
			label: t('reports.side_panel.period.previous_month'),
			value: 'previous'
		}, {
			label: t('reports.side_panel.period.same_month_previous_year'),
			value: 'same_previous_year'
		}];

		do {
			maxDate.subtract(1, 'months'); // Start from the previous month
			monthItems.push({
				label: capitalize(maxDate.format('MMMM YYYY')),
				value: maxDate.format('MM-YYYY')
			});
		}
		while (minDate.isBefore(maxDate, 'month'));

		return monthItems;
	}, [t]);

	const getQuartersItems = useCallback((fromDate: moment.MomentInput, toDate: moment.MomentInput = moment(PERIOD_MINIMUM_DATE, 'YYYY-MM-DD')): PeriodItem[] => {
		const maxDate = moment(fromDate);
		const minDate = moment(toDate);
		const quarterItems = [{
			label: t('reports.side_panel.period.previous_quarter'),
			value: 'previous'
		}, {
			label: t('reports.side_panel.period.same_quarter_previous_year'),
			value: 'same_previous_year'
		}];
	
		do {
			maxDate.subtract(1, 'quarters'); // Start from the previous quarter
			quarterItems.push({
				label: maxDate.format('[Q]Q YYYY'),
				value: maxDate.format('Q-YYYY')
			});
		}
		while (minDate.isBefore(maxDate, 'quarter'));

		return quarterItems;
	}, [t]);

	const setDates = useCallback((value: string) => {
		const isComparePeriodsMode = REPORT_COMPARE_PERIODS_MODES.includes(value as ComparePeriodsMode);
	
		const newSettings: Partial<ReportDefinitionSettings> = isComparePeriodsMode
			? { minDate: null, maxDate: null, comparePeriodsMode: value as ComparePeriodsMode }
			: {
				  minDate: value,
				  maxDate: reportSettings.period === 'monthly'
					  ? moment(filterEndDate).format('MM-YYYY')
					  : moment(filterEndDate).format('Q-YYYY'),
				  comparePeriodsMode: 'custom'
			  };
	
		setReportSettings(newSettings);
	}, [filterEndDate, reportSettings.period, setReportSettings]);

	const isPeriodEnabled = useMemo(() => {
		const filterBegin = moment(filterBeginDate);
		const filterEnd = moment(filterEndDate);
		const beginMonthStart = moment(filterBegin).startOf('month');
		const endMonthEnd = moment(filterEnd).endOf('month');

		if (!filterBegin.isSame(beginMonthStart, 'day') || !filterEnd.isSame(endMonthEnd, 'day')) return false;

		const monthDiff = filterEnd.diff(filterBegin, 'month');
		if (monthDiff !== 0 && monthDiff !== 2) return false;
		if (monthDiff === 2 && ![0, 3, 6, 9].includes(beginMonthStart.month())) return false;

		setReportSettings({period: monthDiff === 0 ? 'monthly' : 'quarterly'});
		return true;
	}, [filterBeginDate, filterEndDate, setReportSettings]);

	const tooltipWrapper = useCallback((children: React.ReactElement, force: boolean = false) => {
		if (force || !isPeriodEnabled) return (
			<Tooltip styleProps={reportForm.powerpoint.period.tooltip} content={t('reports.side_panel.period.add_tooltip')}>
				{children}
			</Tooltip>
		);
		return children;
	}, [isPeriodEnabled, reportForm.powerpoint.period.tooltip, t]);

	const periodSelector = useMemo(() => {
		const choosePeriodLabel = t('reports.side_panel.period.choose_period');
		const filterBegin = moment(filterBeginDate);
		const filterEnd = moment(filterEndDate);
		const monthDiff = filterEnd.diff(filterBegin, 'month');

		const options: PeriodItem[] = monthDiff === 0
			? getMonthsItems(filterBeginDate)
			: getQuartersItems(filterBeginDate);

		// eslint-disable-next-line max-len
		const selectedOption = options.find(option => reportSettings.comparePeriodsMode === 'custom' ? option.value === reportSettings.minDate : option.value === reportSettings.comparePeriodsMode) as PeriodItem;
		
		const [previous, previousYear, ...otherOptions]: PeriodItem[] = options;

		return tooltipWrapper(
			<Box styleProps={{ width: '100%' }}>
				<Select
					id="period-selector"
					optionValue={{ key: selectedOption?.value, value: selectedOption?.label }}
					onChange={value => setDates(value.key)}
					clearable={!disabled && isPeriodEnabled}
					disabled={disabled || !isPeriodEnabled}
					onClear={() => setReportSettings({ minDate: null, maxDate: null, comparePeriodsMode: 'custom' })}
					placeholder={choosePeriodLabel}
				>
					{isPeriodEnabled && !disabled && <>
						<SelectOption
							id="period-selector-previous"
							optionValue={{ key: previous.value, value: previous.label }}
						/>
						<SelectOption
							id="period-selector-previous-year"
							optionValue={{ key: previousYear.value, value: previousYear.label }}
						/>
						<Divider />
						{otherOptions && otherOptions.map((option, index) => (
							<SelectOption
								id={`period-selector-${index+1}`}
								key={index}
								optionValue={{ key: option.value, value: option.label }}
							/>
						))}
					</>}
				</Select>
			</Box>
		);
	}, [
		disabled, isPeriodEnabled, filterBeginDate, filterEndDate, reportSettings,
		tooltipWrapper, setReportSettings, getMonthsItems, getQuartersItems, setDates, t
	]);

	return (
		<>
			<Box className='side-panel-inline'>
				<InputLabel styleProps={{ fontWeight: 600, gap: 4  }}>{t('reports.side_panel.period.label')}{tooltipWrapper(<InfoCircleOutline styleProps={{ strokeWidth: 2 }}/>, true)}</InputLabel>
			</Box>
			<Box className='side-panel-inline'>
				{periodSelector}
			</Box>
		</>
	);
};
